import Image from 'next/image';
import { memo, useCallback, useState } from 'react';

import { type ShortIdProviderProps } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

type PresentationBlock = NonNullable<ShortIdProviderProps['presentationContent']>['blocks'][number];

export type PublicShowcaseImageBlockProps = {
  block: PresentationBlock;
};

export const PublicShowcaseImageBlock = memo(({ block }: PublicShowcaseImageBlockProps) => {
  /*
   * Until the file resolves, `width={0} height={0}` + `h-auto` renders a 0px-tall box, so every
   * image on the page pops from nothing to full height as it decodes. That pushes everything below
   * it down AFTER it mounted — including the virtualized listing grid, whose scroll math is
   * measured against a position that just moved (FOUND-756). The wire carries no intrinsic
   * dimensions on image blocks, so the best we can do is hold a plausible box until the real ratio
   * is known and release it the moment it is.
   */
  const [hasIntrinsicSize, setHasIntrinsicSize] = useState(false);

  const handleLoad = useCallback(() => setHasIntrinsicSize(true), []);

  // The wire guarantees a resolved url on image blocks — an absent url is envelope skew (or a
  // block variant misrouted here) and renders nothing rather than a broken image.
  if (!block.url) {
    return null;
  }

  const extendToEdges = !!block.extendToEdges;
  // Absent roundCorners renders rounded (the authoring default); full-bleed media is always
  // square-edged — a radius on an edge-to-edge image would punch holes into the column edges.
  const hasRoundedCorners = !extendToEdges && block.roundCorners !== false;

  return (
    <div
      // Inset blocks share the links block's px-4 py-3 gutter; extendToEdges renders flush to the
      // content column's edges (the column itself stays width-constrained — same contract as the
      // cover's `fullWidth` variant, which bleeds to the column, not the viewport).
      className={cn('w-full', !extendToEdges && 'px-4 py-3')}
      data-block-id={block.id}
      data-testid="PUBLIC_COLLECTION_IMAGE_BLOCK"
    >
      {/* Decorative authored media — empty alt keeps it out of the accessibility tree.
          unoptimized: the url is a server-resolved, already-sized asset (PublicShowcaseCover
          precedent). width/height 0 + sizes lets the intrinsic ratio drive the rendered height. */}
      <Image
        src={block.url}
        alt=""
        width={0}
        height={0}
        sizes="100vw"
        unoptimized
        onLoad={handleLoad}
        data-testid="PUBLIC_COLLECTION_IMAGE_BLOCK_IMAGE"
        // The radius var follows the link's Corner rounding setting (set on the page root by
        // PublicShowcasePage); the 12px fallback matches the links card's outer radius.
        className={cn(
          'block h-auto w-full',
          hasRoundedCorners && 'rounded-[var(--gallery-card-radius,12px)]',
          // The reserved box above: a 16/9 placeholder tinted with a scale color, so it flips with
          // the page theme on its own. object-contain keeps a decoded-but-not-yet-measured frame
          // from stretching inside it. Both drop away on load, handing the height back to the
          // image's own ratio — the rendered result is byte-identical to before this box existed.
          !hasIntrinsicSize && 'aspect-video bg-gray-3 object-contain',
        )}
      />
    </div>
  );
});

PublicShowcaseImageBlock.displayName = 'PublicShowcaseImageBlock';
